import { ComponentProps } from 'react';
import { SvgIconComponent } from '@mui/icons-material';
import {
  Box,
  SxProps,
  Theme,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { LightTooltip } from '../LightTooltip';

interface Props {
  text: string;
  icon?: SvgIconComponent;
  light?: boolean;
  tooltip?: {
    text: string;
    offset?: [number, number];
    placement?: ComponentProps<typeof Tooltip>['placement'];
  };
  sx?: SxProps<Theme>;
  colorScheme: ColorScheme;
}

export type ColorScheme = 'primary' | 'secondary' | 'grey' | 'warning';

export interface Colors {
  backgroundColor: string;
  textColor: string;
  borderColor: string;
}

function Badge({ text, icon, light, tooltip, sx, colorScheme }: Props) {
  const theme = useTheme();

  const colors: Record<ColorScheme, Colors> = {
    primary: {
      backgroundColor: theme.palette.primary.light,
      textColor: theme.palette.primary.main,
      borderColor: '#FFC2B6',
    },
    secondary: {
      backgroundColor: theme.palette.accent.main,
      textColor: theme.palette.secondary.main,
      borderColor: '#AAF2DA',
    },
    grey: {
      backgroundColor: theme.palette.lightGrey.main,
      textColor: theme.palette.darkGrey.main,
      borderColor: theme.palette.lightOutlineGrey.main,
    },
    warning: {
      backgroundColor: theme.palette.warning.light,
      textColor: theme.palette.warning.contrastText,
      borderColor: theme.palette.warning.main,
    },
  };

  const TooltipComponent = light ? LightTooltip : Tooltip;

  const { backgroundColor, textColor, borderColor } = colors[colorScheme];

  const renderIcon = () => {
    if (!icon) return null;
    const SvgIcon = icon as SvgIconComponent;
    return (
      <SvgIcon
        sx={{
          color: textColor,
          width: '16px',
          height: '16px',
        }}
      />
    );
  };

  return (
    <TooltipComponent
      title={tooltip?.text}
      placement={tooltip?.placement ?? 'bottom'}
      arrow
      slotProps={{
        popper: {
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: tooltip?.offset,
              },
            },
          ],
        },
      }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          padding: '2px 6px',
          backgroundColor,
          borderRadius: '4px',
          gap: '4px',
          borderColor,
          borderWidth: '1px',
          borderStyle: 'solid',
          ...sx,
        }}>
        <Typography
          variant="h5"
          sx={{
            color: textColor,
            fontSize: '12px',
            lineHeight: '12px',
          }}>
          {text}
        </Typography>
        {renderIcon()}
      </Box>
    </TooltipComponent>
  );
}

export default Badge;
